import React, { useState, useRef } from 'react';
import { Box, Button, InputBase, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import color from 'src/utils/colors';
import { SlCheckbox } from 'src/components/BeaconV2Components/SlCheckbox/SlCheckbox';
import { Controller, useFormContext, FieldPathByValue, useWatch } from 'react-hook-form';
import { KeywordBulkUploadModal } from './KeywordBulkUploadModal';

const StyledContainer = styled(Box)({
  border: `1px solid ${color.graysSecondary}`,
  borderRadius: '8px',
  padding: '24px 16px',
  backgroundColor: 'white'
});

const StacklineRecommendedSection = styled(Box)({
  backgroundColor: color.contentGenBackground,
  padding: '16px',
  borderRadius: '8px',
  marginBottom: '24px'
});

const SearchInputContainer = styled(Box)({
  backgroundColor: 'white',
  border: `1px solid ${color.graysSecondary}`,
  borderRadius: '4px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden'
});

const SearchInput = styled(InputBase)({
  flex: 1,
  fontSize: '14px',
  fontFamily: 'Roboto',
  '& input': {
    padding: 0,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.08px',
    color: color.darkBlue
  },
  '& input::placeholder': {
    color: color.blueGrey,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.08px',
    opacity: 1
  }
});

const BulkUploadButton = styled(Button)({
  backgroundColor: color.graysPrimary,
  border: `1px solid ${color.graysSecondary}`,
  borderRadius: 0,
  height: '40px',
  padding: '3px 16px',
  fontSize: '14px',
  fontWeight: 500,
  color: color.darkBlue,
  textTransform: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: color.graysSecondary
  }
});

const KeywordBankSection = styled(Box)({
  backgroundColor: color.contentGenBackground,
  padding: '16px',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px'
});

const InclusionChip = styled(Chip)({
  backgroundColor: color.lightOpaqueBlue,
  fontSize: '14px',
  fontWeight: 500,
  height: '28px',
  borderRadius: '24px',
  '& .MuiChip-label': {
    paddingLeft: '12px',
    paddingRight: '8px',
    color: color.darkBlue,
    lineHeight: '20px'
  },
  '& .MuiChip-deleteIcon': {
    fontSize: '16px',
    color: color.darkBlue,
    margin: '0',
    marginRight: '8px',
    '&:hover': {
      color: color.darkBlue
    }
  }
});

const ExclusionChip = styled(Chip)({
  backgroundColor: color.opaqueGrey,
  fontSize: '14px',
  fontWeight: 500,
  height: '28px',
  borderRadius: '24px',
  '& .MuiChip-label': {
    paddingLeft: '12px',
    paddingRight: '8px',
    color: color.graysSecondaryText,
    lineHeight: '20px'
  },
  '& .MuiChip-deleteIcon': {
    fontSize: '16px',
    color: color.graysSecondaryText,
    margin: '0',
    marginRight: '8px',
    '&:hover': {
      color: color.graysSecondaryText
    }
  }
});

interface KeywordCardProps<T extends Record<string, unknown>> {
  title: string;
  description: string;
  keywordBankTitle: string;
  keywordBankDescription: string;
  showStacklineRecommended?: boolean;
  fieldName: FieldPathByValue<T, string[]>;
  type: 'inclusion' | 'exclusion';
  readOnly?: boolean;
}

function KeywordCard<T extends Record<string, unknown>>({
  title,
  description,
  keywordBankTitle,
  keywordBankDescription,
  showStacklineRecommended = false,
  fieldName,
  type,
  readOnly = false
}: KeywordCardProps<T>) {
  const { control } = useFormContext<T>();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);

  const handleKeyPress = (event: React.KeyboardEvent, currentValue: string[], onChange: (value: string[]) => void) => {
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      event.preventDefault();
      const trimmedValue = inputValue.trim();

      // Check if the keyword already exists
      if (!currentValue.includes(trimmedValue)) {
        onChange([...currentValue, trimmedValue]);
      }

      setInputValue('');
    }
  };

  const handleDeleteKeyword = (indexToDelete: number, currentValue: string[], onChange: (value: string[]) => void) => {
    const newValue = currentValue.filter((_, index) => index !== indexToDelete);
    onChange(newValue);
  };

  const handleBulkUploadSave = (keywords: string[], onChange: (value: string[]) => void, currentValue: string[]) => {
    // Merge new keywords with existing ones, avoiding duplicates
    const existingKeywords = currentValue || [];
    const uniqueNewKeywords = keywords.filter((keyword) => !existingKeywords.includes(keyword));
    onChange([...existingKeywords, ...uniqueNewKeywords]);
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field }) => {
        // `field.value` is inferred as a generic PathValue; at runtime this is a string[] for our keyword fields.
        // Narrow safely for rendering/handlers.
        const keywords = (Array.isArray(field.value) ? field.value : []) as string[];

        return (
          <StyledContainer>
          {showStacklineRecommended && !readOnly && (
            <StacklineRecommendedSection>
              <Flex gap={16} alignItems="center">
                <SlCheckbox
                  checked
                  disabled
                  readOnly
                  iconProps={{ width: '16px', height: '16px' }}
                  style={{ cursor: 'default' }}
                />
                <Flex flexDirection="column" gap={4}>
                  <Text sx={{ fontWeight: 700, fontSize: '14px', lineHeight: '20px' }}>
                    Stackline Recommended Keywords
                  </Text>
                  <Text sx={{ fontSize: '14px', lineHeight: '20px' }}>
                    {"Boost your new content with Stackline's AI-powered recommended keywords."}
                  </Text>
                </Flex>
              </Flex>
            </StacklineRecommendedSection>
          )}

          <Flex flexDirection="column" gap={16}>
            {/* Header */}
            <Flex flexDirection="column" gap={8}>
              <Text sx={{ fontWeight: 700, fontSize: '16px', lineHeight: '24px' }}>{title}</Text>
              <Text sx={{ fontSize: '16px', lineHeight: '24px' }}>{description}</Text>
            </Flex>

            {/* Search Input with Bulk Upload */}
            {!readOnly && (
              <SearchInputContainer>
                <SearchInput
                  ref={inputRef}
                  placeholder="Enter keyword or phrase"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, keywords, field.onChange)}
                  sx={{ flex: 1, paddingLeft: '12px' }}
                />
                <BulkUploadButton onClick={() => setBulkUploadModalOpen(true)} disableRipple>
                  Bulk Upload
                </BulkUploadButton>
              </SearchInputContainer>
            )}
          </Flex>

          {/* Keyword Bank */}
          <KeywordBankSection sx={{ marginTop: '24px' }}>
            <Flex flexDirection="column" gap={4}>
              <Text sx={{ fontWeight: 700, fontSize: '14px', lineHeight: '20px' }}>{keywordBankTitle}</Text>
              {keywords.length === 0 && (
                <Text sx={{ fontSize: '14px', lineHeight: '20px' }}>{keywordBankDescription}</Text>
              )}
            </Flex>

            {/* Display keywords as chips */}
            {keywords.length > 0 && (
              <Flex flexWrap="wrap" rowGap="12px" columnGap="8px">
                {keywords.map((keyword: string, index: number) => {
                  const ChipComponent = type === 'inclusion' ? InclusionChip : ExclusionChip;
                  return (
                    <ChipComponent
                      key={index}
                      label={keyword}
                      onDelete={readOnly ? undefined : () => handleDeleteKeyword(index, keywords, field.onChange)}
                    />
                  );
                })}
              </Flex>
            )}
          </KeywordBankSection>

          {/* Bulk Upload Modal */}
          <KeywordBulkUploadModal
            open={bulkUploadModalOpen}
            onClose={() => setBulkUploadModalOpen(false)}
            type={type}
            onSave={(newKeywords) => handleBulkUploadSave(newKeywords, field.onChange, keywords)}
          />
        </StyledContainer>
        );
      }}
    />
  );
}

interface KeywordsSectionProps<T extends Record<string, unknown>> {
  inclusionFieldName: FieldPathByValue<T, string[]>;
  exclusionFieldName: FieldPathByValue<T, string[]>;
  readOnly?: boolean;
}

export function KeywordsSection<T extends Record<string, unknown>>({
  inclusionFieldName,
  exclusionFieldName,
  readOnly = false
}: KeywordsSectionProps<T>) {
  const { control } = useFormContext<T>();
  const inclusionKeywords = useWatch({ control, name: inclusionFieldName });
  const exclusionKeywords = useWatch({ control, name: exclusionFieldName });

  const shouldShowInclusionCard = !readOnly || (Array.isArray(inclusionKeywords) && inclusionKeywords.length > 0);
  const shouldShowExclusionCard = !readOnly || (Array.isArray(exclusionKeywords) && exclusionKeywords.length > 0);

  if (!shouldShowInclusionCard && !shouldShowExclusionCard) {
    return null;
  }

  return (
    <Flex flexDirection="column" gap={24}>
      {shouldShowInclusionCard && (
        <KeywordCard<T>
          title="Keyword & Phrase Inclusion List"
          description="Add the keywords or phrases that should be included in the new content for products under this profile."
          keywordBankTitle="Included Keywords"
          keywordBankDescription="All keywords you wish to include will appear here."
          showStacklineRecommended={!readOnly}
          fieldName={inclusionFieldName}
          type="inclusion"
          readOnly={readOnly}
        />
      )}
      {shouldShowExclusionCard && (
        <KeywordCard<T>
          title="Keyword & Phrase Exclusion List"
          description="Add the keywords or phrases that should be excluded in the new content for products under this profile. Stackline will automatically remove retailer-restricted terms."
          keywordBankTitle="Excluded Keywords"
          keywordBankDescription="All keywords you wish to exclude will appear here."
          fieldName={exclusionFieldName}
          type="exclusion"
          readOnly={readOnly}
        />
      )}
    </Flex>
  );
}
